

import { useState, useEffect, useMemo } from "react";
import { 
  Plus, 
  Pencil, 
  Package, 
  BookOpen,
  Store,
  Eye,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Download,
  CheckSquare,
  Square,
  Power,
  Search
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import VendorForm from "./VendorForm";
import ServiceForm from "./ServiceForm";
import PaymentLedger from "./PaymentLedger";
import VendorDetailModal from "./VendorDetailModal";
import { vendorsApi, mastersApi, apiErrorMessage } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { formatCurrency } from "@/lib/data";
import { byNewest } from "@/lib/utils";
import { VendorFilters, VendorsTable, BulkActionsBar, SortControls, PaginationControls, VendorsHeader, VendorsModals } from "./components";
import type { Vendor } from "./types";

// 'newest' = created_at desc (default); the rest are user-chosen column sorts
type VendorSortField = 'newest' | 'name' | 'total_assigned' | 'total_paid' | 'balance' | 'status';




export default function VendorsPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  const canDelete = user?.role === 'admin' || user?.role === 'master' || user?.role === 'super_admin';
  
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Filter and sort state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortField, setSortField] = useState<VendorSortField>('newest');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Bulk actions state
  const [selectedVendors, setSelectedVendors] = useState<Set<number>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isLedgerModalOpen, setIsLedgerModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [detailVendorId, setDetailVendorId] = useState<number | null>(null);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [vendorServices, setVendorServices] = useState<any[]>([]);

  // Delete confirmation state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingVendor, setDeletingVendor] = useState<Vendor | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    try {
      setLoading(true);
      const response = await vendorsApi.list();
      const vendorsData = response.data?.data || response.data || [];
      setVendors(vendorsData);
    } catch (error) {
      showToast("Failed to load vendors", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle sort
  const handleSort = (field: VendorSortField) => {
    if (field === 'newest') {
      // "Newest" is a fixed ordering (latest created first), not a toggle
      setSortField('newest');
      setSortDirection('desc');
    } else if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: VendorSortField) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-stone-400" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4 text-amber-600" /> : 
      <ArrowDown className="w-4 h-4 text-amber-600" />;
  };

  const openDeleteModal = (vendor: Vendor) => {
    setDeletingVendor(vendor);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingVendor) return;
    
    setDeleteLoading(true);
    const previousVendors = vendors;
    setVendors(prev => prev.filter(v => v.id !== deletingVendor.id));
    
    try {
      await vendorsApi.delete(deletingVendor.id);
      showToast("Vendor deleted successfully", "success");
      setDeleteModalOpen(false);
      setDeletingVendor(null);
    } catch (error) {
      setVendors(previousVendors);
      showToast(apiErrorMessage(error, "Couldn't delete this vendor"), "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (data: any) => {
    // The form stays open until the server has saved, so a refused save
    // (e.g. a duplicate name) doesn't lose what was typed
    try {
      if (editingVendor) {
        await vendorsApi.update(editingVendor.id, data);
        setVendors(prev => prev.map(v => v.id === editingVendor.id ? { ...v, ...data } as Vendor : v));
        showToast("Vendor updated successfully", "success");
        setIsModalOpen(false);
        setEditingVendor(null);
      } else {
        // Optimistic update - add placeholder immediately
        const tempId = Date.now();
        const optimisticVendor = {
          id: tempId,
          ...data,
          status: data.status || 'active',
          created_at: new Date().toISOString(), // keeps the placeholder at the top under "Newest" sort
          balance: 0,
          total_paid: 0
        } as Vendor;
        setVendors(prev => [optimisticVendor, ...prev]);
        
        const response = await vendorsApi.create(data);
        const newVendor = response.data?.data || response.data;
        
        // Replace temp vendor with real one
        setVendors(prev => prev.map(v => v.id === tempId ? newVendor : v));
        showToast("Vendor created successfully", "success");
        setIsModalOpen(false);
      }
    } catch (error) {
      showToast(apiErrorMessage(error, "Couldn't save the vendor"), "error");
      loadVendors();
    }
  };

  const handleDeleteService = async (serviceId: number) => {
    try {
      await mastersApi.services.delete(serviceId);
      setVendorServices(prev => prev.filter((s: any) => s.id !== serviceId));
      showToast("Service deleted", "success");
    } catch (error) {
      // Services already used on events or packages are archived instead of deleted
      showToast(apiErrorMessage(error, "Couldn't delete this service"), "error");
    }
  };

  const loadServices = async (vendorId: number) => {
    try {
      const response = await mastersApi.services.list();
      const allServices = response.data?.data || response.data || [];
      const vendorSpecificServices = allServices.filter((s: any) => s.vendor_id === vendorId);
      setVendorServices(vendorSpecificServices);
    } catch (error) {
      console.error("Failed to load services:", error);
    }
  };

  const handleServiceSubmit = async (data: any) => {
    try {
      if (selectedVendor) {
        await mastersApi.services.create({ ...data, vendor_id: selectedVendor.id });
        showToast("Service added successfully", "success");
        loadServices(selectedVendor.id);
      }
    } catch (error) {
      showToast("Failed to add service", "error");
    }
  };

  const openServices = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    loadServices(vendor.id);
    setIsServiceModalOpen(true);
  };

  const openLedger = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setIsLedgerModalOpen(true);
  };

  // Filter and sort vendors
  const filteredAndSortedVendors = useMemo(() => {
    let filtered = vendors;
    
    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(v => 
        (v.name || "").toLowerCase().includes(searchLower) ||
        (v.contact_person || "").toLowerCase().includes(searchLower) ||
        (v.vendor_type?.name || "").toLowerCase().includes(searchLower) ||
        (v.phone || "").includes(searchLower)
      );
    }
    
    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(v => v.status === 'active');
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(v => v.status === 'inactive');
    }
    
    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'newest':
          // ascending = oldest first; the default 'desc' direction flips it to newest first
          comparison = byNewest(b, a);
          break;
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'total_assigned':
          comparison = (a.total_assigned || 0) - (b.total_assigned || 0);
          break;
        case 'total_paid':
          comparison = (a.total_paid || 0) - (b.total_paid || 0);
          break;
        case 'balance':
          comparison = (a.balance || 0) - (b.balance || 0);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [vendors, search, statusFilter, sortField, sortDirection]);

  // Pagination
  const paginatedVendors = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedVendors.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedVendors, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedVendors.length / itemsPerPage);
  
  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedVendors(new Set()); // Clear selection on filter change
  }, [search, statusFilter, sortField, sortDirection]);
  
  // Show bulk actions when items are selected
  useEffect(() => {
    setShowBulkActions(selectedVendors.size > 0);
  }, [selectedVendors]);
  
  // Handle bulk selection
  const toggleVendorSelection = (id: number) => {
    setSelectedVendors(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  
  const toggleAllVendors = () => {
    if (selectedVendors.size === paginatedVendors.length) {
      setSelectedVendors(new Set());
    } else {
      setSelectedVendors(new Set(paginatedVendors.map(v => v.id)));
    }
  };
  
  // Handle bulk toggle status
  const handleBulkToggleStatus = async (newStatus: string) => {

    try {
      for (const id of selectedVendors) {
        const vendor = vendors.find(v => v.id === id);
        if (vendor) {
          await vendorsApi.update(id, { ...vendor, status: newStatus });
        }
      }
      showToast(`${selectedVendors.size} vendors updated`, "success");
      setSelectedVendors(new Set());
      loadVendors();
    } catch (error) {
      showToast("Failed to update vendors", "error");
    }
  };
  
  // Handle bulk delete
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const handleBulkDelete = () => setBulkDeleteOpen(true);
  const confirmBulkDelete = async () => {
    setBulkDeleteOpen(false);

    try {
      for (const id of selectedVendors) {
        await vendorsApi.delete(id);
      }
      showToast(`${selectedVendors.size} vendors deleted`, "success");
      setSelectedVendors(new Set());
      loadVendors();
    } catch (error) {
      showToast("Failed to delete vendors", "error");
    }
  };
  
  // Handle CSV export
  const handleExportCSV = () => {
    const vendorsToExport = selectedVendors.size > 0 
      ? vendors.filter(v => selectedVendors.has(v.id))
      : filteredAndSortedVendors;
    
    const headers = ['Name', 'Type', 'Contact Person', 'Phone', 'Email', 'Assigned', 'Paid', 'Balance', 'Status'];
    const rows = vendorsToExport.map(vendor => [
      vendor.name,
      vendor.vendor_type?.name || '',
      vendor.contact_person,
      vendor.phone,
      vendor.email || '',
      vendor.total_assigned || 0,
      vendor.total_paid || 0,
      vendor.balance || 0,
      vendor.status,
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `vendors-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    if (selectedVendors.size > 0) {
      setSelectedVendors(new Set());
    }
    showToast('CSV exported successfully', 'success');
  };

  if (loading) {
    return (
      <DashboardLayout>
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div>
            <div className="h-6 w-32 bg-stone-200 rounded animate-pulse mb-2" />
            <div className="h-4 w-48 bg-stone-200 rounded animate-pulse" />
          </div>
          <div className="h-9 w-32 bg-stone-200 rounded-xl animate-pulse" />
        </div>

        {/* Filters Skeleton */}
        <div className="h-16 bg-stone-100 rounded-xl border border-stone-200 mb-4 animate-pulse" />

        {/* Sort Controls Skeleton */}
        <div className="h-10 bg-stone-100 rounded-lg mb-4 animate-pulse" />

        {/* Table Skeleton */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="h-12 bg-stone-100 border-b border-stone-200 animate-pulse" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 border-b border-stone-100 animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <VendorsHeader
        isStaff={isStaff}
        onExport={handleExportCSV}
        onAddVendor={() => { setEditingVendor(null); setIsModalOpen(true); }}
      />

      {/* Filters */}
      <VendorFilters
        search={search}
        setSearch={setSearch}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        onApply={() => {}} // Filters are applied reactively
      />

      {showBulkActions && !isStaff && (
        <BulkActionsBar
          selectedCount={selectedVendors.size}
          onSetActive={() => handleBulkToggleStatus('active')}
          onSetInactive={() => handleBulkToggleStatus('inactive')}
          onDelete={handleBulkDelete}
          onCancel={() => setSelectedVendors(new Set())}
        />
      )}

      <SortControls
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={(value) => {
          setItemsPerPage(value);
          setCurrentPage(1);
        }}
      />

      <VendorsTable
        vendors={paginatedVendors}
        selectedVendors={selectedVendors}
        isStaff={isStaff}
        canDelete={canDelete}
        onToggleSelection={toggleVendorSelection}
        onToggleAll={toggleAllVendors}
        onViewDetails={(id) => { setDetailVendorId(id); setIsDetailModalOpen(true); }}
        onOpenServices={openServices}
        onOpenLedger={openLedger}
        onEdit={(vendor) => { setEditingVendor(vendor); setIsModalOpen(true); }}
        onDelete={openDeleteModal}
      />

      {paginatedVendors.length === 0 && (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Store className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">
            {search || statusFilter ? 'No vendors match your filters' : 'No vendors yet'}
          </p>
          {!isStaff && !search && !statusFilter && (
            <button 
              onClick={() => setIsModalOpen(true)}
              className="mt-3 px-4 py-2 rounded-xl gold-gradient text-white text-sm font-medium"
            >
              Add First Vendor
            </button>
          )}
        </div>
      )}

      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={filteredAndSortedVendors.length}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
      />

      <VendorsModals
        isModalOpen={isModalOpen}
        isServiceModalOpen={isServiceModalOpen}
        isLedgerModalOpen={isLedgerModalOpen}
        isDetailModalOpen={isDetailModalOpen}
        detailVendorId={detailVendorId}
        editingVendor={editingVendor}
        selectedVendor={selectedVendor}
        vendorServices={vendorServices}
        deleteModalOpen={deleteModalOpen}
        deletingVendor={deletingVendor}
        deleteLoading={deleteLoading}
        onCloseModal={() => { setIsModalOpen(false); setEditingVendor(null); }}
        onCloseServiceModal={() => { setIsServiceModalOpen(false); setSelectedVendor(null); }}
        onCloseLedgerModal={() => { setIsLedgerModalOpen(false); setSelectedVendor(null); }}
        onCloseDetailModal={() => { setIsDetailModalOpen(false); setDetailVendorId(null); }}
        onVendorUpdated={loadVendors}
        onFormSubmit={handleFormSubmit}
        onServiceSubmit={handleServiceSubmit}
        onDeleteConfirm={handleDelete}
        onDeleteCancel={() => { setDeleteModalOpen(false); setDeletingVendor(null); }}
        onDeleteService={handleDeleteService}
      />

      <DeleteConfirmationModal
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={confirmBulkDelete}
        title={`Delete ${selectedVendors.size} vendor${selectedVendors.size === 1 ? "" : "s"}?`}
        message="Vendors that already have events or payments can't be deleted; set them to inactive instead."
      />
    </DashboardLayout>
  );
}
